import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

const EMPTY = { name: "", address: "", phone: "", email: "", website: "", npi: "" };
const FIELDS = [
  ["name", "Name", true], ["address", "Address", true], ["phone", "Phone", true],
  ["email", "Email", true], ["website", "Website", false], ["npi", "NPI", false],
];

function flagCount(flags) {
  try {
    const parsed = typeof flags === "string" ? JSON.parse(flags || "{}") : flags || {};
    return Object.values(parsed).filter(Boolean).length;
  } catch {
    return 0;
  }
}

function OrganizationsPanel({ apiBase, organizations, onChanged }) {
  const [editing, setEditing] = useState(null); // null | "new" | org id
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const open = async (org) => {
    setError("");
    if (!org) {
      setForm(EMPTY);
      setEditing("new");
      return;
    }
    const res = await fetch(`${apiBase}/organizations/${org.id}`);
    const full = res.ok ? await res.json() : org;
    setForm(Object.fromEntries(FIELDS.map(([k]) => [k, full[k] || ""])));
    setEditing(org.id);
  };

  const request = async (url, method, body) => {
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      throw new Error(typeof detail.detail === "string" ? detail.detail : "Save failed");
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      if (editing === "new") await request(`${apiBase}/organizations`, "POST", form);
      else await request(`${apiBase}/organizations/${editing}`, "PUT", form);
      setEditing(null);
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (org) => {
    await request(`${apiBase}/organizations/${org.id}`, "PUT", { is_active: !org.is_active }).catch(() => {});
    onChanged();
  };

  const missingRequired = FIELDS.some(([k, , required]) => required && !String(form[k]).trim());

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Organizations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => open(null)}>Add organization</Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Features on</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {organizations.map((org) => (
            <TableRow key={org.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>{org.name}</Typography>
                <Typography variant="caption" color="text.secondary">{org.address}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" display="block">{org.phone}</Typography>
                <Typography variant="caption" color="text.secondary">{org.email}</Typography>
              </TableCell>
              <TableCell><Chip size="small" label={flagCount(org.feature_flags)} /></TableCell>
              <TableCell>
                <Switch size="small" checked={!!org.is_active} onChange={() => toggleActive(org)}
                  inputProps={{ "aria-label": `${org.name} active` }} />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => open(org)} aria-label={`Edit ${org.name}`}><EditIcon fontSize="small" /></IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing === "new" ? "Add organization" : "Edit organization"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
          {error && <Alert severity="error">{error}</Alert>}
          {FIELDS.map(([key, label, required]) => (
            <TextField key={key} label={label} required={required} size="small" value={form[key]}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || missingRequired}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

OrganizationsPanel.propTypes = {
  apiBase: PropTypes.string.isRequired,
  organizations: PropTypes.array.isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default OrganizationsPanel;
